"use client"

import { useState, useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { TagSelectionModal } from "./tag-selection-modal"

interface HitomiTagTickerProps {
  tags: string[]
  onTagsSelected?: (includeTags: string[], excludeTags: string[]) => void
  className?: string
}

export function HitomiTagTicker({ tags, onTagsSelected, className }: HitomiTagTickerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const tickerRef = useRef<HTMLDivElement>(null)
  const [tickerWidth, setTickerWidth] = useState(0)

  useEffect(() => {
    if (tickerRef.current) {
      // Calculate total width of all tags
      const totalWidth = tickerRef.current.scrollWidth
      setTickerWidth(totalWidth)
    }
  }, [tags])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering parent click handlers
    setIsModalOpen(true)
  }

  const handleTagsSelected = (includeTags: string[], excludeTags: string[]) => {
    onTagsSelected?.(includeTags, excludeTags)
    setIsModalOpen(false)
  }

  // Create duplicated tags for seamless loop
  const duplicatedTags = [...tags, ...tags]

  return (
    <>
      <button
        onClick={handleClick}
        className={cn(
          "relative overflow-hidden bg-secondary/50 hover:bg-secondary/70 transition-colors",
          "rounded px-2 py-1.5 cursor-pointer w-full",
          "flex items-center",
          className
        )}
      >
        {/* Soft fade at both ends: the tags slide past the edges, so a hard cut looked like clipped text. */}
        <div
          className="relative w-full overflow-hidden"
          style={{ maskImage: "linear-gradient(to right, transparent, #000 12px, #000 calc(100% - 12px), transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, #000 12px, #000 calc(100% - 12px), transparent)" }}
        >
          <div
            ref={tickerRef}
            className="flex gap-1 animate-scroll-tags whitespace-nowrap"
            style={{
              animationDuration: `${Math.max(8, tickerWidth / 100)}s`,
            }}
          >
            {duplicatedTags.map((tag, idx) => (
              <span
                key={`${tag}-${idx}`}
                className="inline-flex items-center text-xs font-medium text-foreground/80"
              >
                {tag}
                {idx < duplicatedTags.length - 1 && (
                  <span className="mx-1 text-muted-foreground">•</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </button>

      <TagSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tags={tags}
        onConfirm={handleTagsSelected}
      />
    </>
  )
}
